import { yupResolver } from '@hookform/resolvers/yup'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import SaveAsIcon from '@mui/icons-material/SaveAs'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Stack, TextField, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import axiosClient from 'utils/axiosClient'
import * as yup from 'yup'
import CancelIcon from '@mui/icons-material/Cancel'

type Props = {
  apiEndpoint?: string
  fieldName?: string
  label: string
  value?: string
  max?: number
  min?: number
  multiline?: boolean
  rows?: number
  required?: boolean
}
interface FormInput {
  name: string
}

export default function EditInput({
  apiEndpoint,
  fieldName,
  max,
  min,
  multiline,
  rows,
  label,
  value = '',
  required = false,
}: Props) {
  const { enqueueSnackbar } = useSnackbar()
  const schema = yup.object({
    name: yup
      .string()
      .max(max || 9999)
      .min(min || required ? 1 : 0, !min && required ? '必要項目' : undefined),
  })
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: value,
    },
  })
  const [loading, setLoading] = useState(false)
  const [edit, setEdit] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (ref.current) {
      if (edit) {
        ref.current.querySelector('input')?.focus()
        ref.current.querySelector('input')?.focus()
      } else {
        ref.current.querySelector('input')?.blur()
        ref.current.querySelector('input')?.blur()
      }
    }
    reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit])
  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    if (apiEndpoint && fieldName) {
      setLoading(true)
      try {
        await axiosClient.put(apiEndpoint, {
          data: JSON.stringify({ [fieldName]: data.name }),
        })
        setEdit(false)
        const msg = label + 'を修正しました。'
        enqueueSnackbar(msg, {
          variant: 'success',
        })
        setLoading(false)
      } catch (_error) {
        const msg = 'エラーが発生しました。'
        enqueueSnackbar(msg, {
          variant: 'error',
        })
      }
    }
  }
  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      onSubmit={handleSubmit(onSubmit)}
      my={2}
    >
      {edit ? (
        <Stack
          direction={{ md: 'row', xs: 'column' }}
          alignItems="center"
          sx={{ display: edit ? 'flex' : 'none' }}
          ref={ref}
        >
          <TextField
            variant={multiline ? 'outlined' : 'standard'}
            required={required}
            margin="dense"
            fullWidth
            label={label}
            multiline={multiline}
            rows={rows}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setEdit(false)
                setValue('name', value, {
                  shouldValidate: true,
                })
              }
            }}
            {...register('name')}
            error={'name' in errors}
            helperText={errors.name?.message}
          />
          <Stack direction="row">
            <Button
              sx={{
                mx: 1,
                px: 1,
                minWidth: 0,
              }}
              size="small"
              color="error"
              variant="contained"
              onClick={() => {
                setEdit(false)
                setValue('name', value, {
                  shouldValidate: true,
                })
              }}
            >
              <CancelIcon />
            </Button>
            <LoadingButton
              disabled={Object.keys(errors).length > 0}
              loading={loading}
              sx={{
                mx: 1,
                px: 1,
                minWidth: 0,
              }}
              size="small"
              variant="contained"
              type="submit"
            >
              <SaveAsIcon />
            </LoadingButton>
          </Stack>
        </Stack>
      ) : (
        <Stack
          direction={{ md: 'row', xs: 'column' }}
          spacing={2}
          sx={{ display: !edit ? 'flex' : 'none' }}
          alignItems={{
            md: 'center',
            xs: 'flex-start',
          }}
        >
          <Typography fontWeight="bold" variant="body2" flex={'none'}>
            {label}
          </Typography>
          <Stack direction="row" alignItems="center">
            <Typography
              sx={{
                color: Boolean(value.trim()) ? '' : '#aaa',
              }}
            >
              {value.trim() || '未設定'}
            </Typography>
            <Button
              sx={{
                flex: 'none',
                mx: 1,
                p: 0.5,
                minWidth: 0,
              }}
              size="small"
              variant="contained"
              color="info"
              onClick={() => {
                setEdit(true)
              }}
            >
              <ModeEditIcon fontSize="small" />
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  )
}
